import { GenericAbortSignal } from "axios";
import { MprcAutoCompleteHierarchical } from "components/MprcAutoCompleteHierarchical";
import { IAutocompleteHierarchicalBaseProps } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchical";
import { AutoCompleteHierarchicalModel, AutoCompleteHierarchicalSuggestions, TopicAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { UserSelectionEntityType, UserSelectionEntry } from "generated/CoreApi_v1";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////

interface IProps extends IAutocompleteHierarchicalBaseProps {
    useHistory?: boolean;
    saveHistory?: boolean;
    isInterestAssigment?: boolean;
    applyKnowledgeTests?: boolean;
    exceptTopicIds?: string[];
    margin?: MuiMargin;
    onGetSuggestion?: (parentId: string | null, trySelectionHistory: boolean, searchText: string, pageNumber: number, pageSize: number, signal: GenericAbortSignal | undefined) => Promise<AutoCompleteHierarchicalSuggestions<AutoCompleteHierarchicalModel>>;
    onChange: (item: (AutoCompleteHierarchicalModel | TopicAutoCompleteHierarchicalModel)[]) => void;
    onError: (error: any) => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Auto complete hierarquico de tópicos
 * @param props
 */
export const TopicAutoCompleteHierarchical: React.FC<IProps> = (props) => {

    const { t } = useTranslation();
    const api = useCoreApi();
    const session = useSession();

    const { label, placeholder, onChange, onError,
        required = false,
        disabled = false,
        selectedMode = 'free',
        useHistory = false,
        saveHistory = true,
        exceptTopicIds = [],
        isInterestAssigment = false,
        showDescendantOption = false,
        applyKnowledgeTests = false,
        margin,
        onGetSuggestion,
        ...rest
    } = props;

    const [saveSelection, setSaveSelection] = useState<TopicAutoCompleteHierarchicalModel[] | undefined>(undefined);

    /// Carrega a lista de sugestões
    const getSuggestions = (parentId: string | null, trySelectionHistory: boolean, searchText: string, pageNumber: number, pageSize: number, signal: GenericAbortSignal | undefined): Promise<AutoCompleteHierarchicalSuggestions<AutoCompleteHierarchicalModel>> => {
        searchText = searchText.toLowerCase();

        return new Promise<AutoCompleteHierarchicalSuggestions<TopicAutoCompleteHierarchicalModel>>((resolve) => {
            api.v1.suggestions.topicHierarchical({
                parentId: parentId,
                trySelectionHistory: trySelectionHistory && useHistory === true,
                isInterestAssigment: isInterestAssigment,
                applyKnowledgeTests,
                searchText: searchText,
                exceptTopicIds: exceptTopicIds ?? [],
                pageNumber: pageNumber,
                pageSize: pageSize,
            }, { signal })
                .then(result => {
                    resolve({
                        items: result.data.items as TopicAutoCompleteHierarchicalModel[],
                        totalRows: result.data.totalRows
                    });
                })
                .catch(error => {
                    if (!isCanceledError(error)) {
                        resolve({ items: [], totalRows: 0 });
                        onError(error);
                    }
                });
        });
    };

    const handleChange = (items: AutoCompleteHierarchicalModel[]) => {
        if (saveHistory === true) setSaveSelection(items as TopicAutoCompleteHierarchicalModel[]);
        onChange(items);
    };

    const handleDisplayLabel = (option: TopicAutoCompleteHierarchicalModel): string => {
        return option.label;
    };

    // const handleDisplayTooltip = (item: TopicAutoCompleteHierarchicalModel): string => {
    //     const tree = item.tree?.filter((el, index) => index > 0) ?? [];
    //     return tree && tree.length > 0 ? `${tree.map(x => x.parentLabel).join("\\")}\\${item.label}` : item.label;
    // };


    /// Salva itens selecionados.
    useEffect(() => {
        if (saveSelection != undefined) {
            var payload = {
                userId: session.user.userId,
                entries: saveSelection.map((v) => ({
                    entityType: UserSelectionEntityType.Topic,
                    entityId: v.key,
                    otherInfo: 'false'
                } as UserSelectionEntry))
            };

            api.v1.user.saveSelectionHistory(payload)
                .then(() => {
                    setSaveSelection(undefined);
                })
                .catch((e) => {
                    setSaveSelection(undefined);
                });
        }
    }, [saveSelection]);


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcAutoCompleteHierarchical
                {...rest}
                required={required}
                disabled={disabled}
                margin={margin}
                selectedMode={selectedMode}
                showDescendantOption={showDescendantOption}
                label={label ?? ""}
                placeholder={placeholder}
                onGetSuggestions={onGetSuggestion ?? getSuggestions}
                onChange={handleChange}
                onDisplayLabel={(option) => handleDisplayLabel(option as TopicAutoCompleteHierarchicalModel)}
                grouperItemCannotBeSelectedMessage={t("core:Topic.TopicAutoComplete.GrouperItemCannotBeSelectedMessage")}
                itemCannotBeSelectedMessage={t("core:Topic.TopicAutoComplete.ItemCannotBeSelectedMessage")}
            />
        </>
    );
};
